import * as React from 'react';
import clsx from 'clsx';
import Image from 'next/image';
import { ReduxState } from 'ducks';
import { DigitalMap, DigitalMapPin } from 'models/digitalMap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMapDisplayContext } from './MapDisplayContext';
import { useItineraries } from './useItineraries';
import { useUrlQueryDisplayState } from './useUrlQueryDisplayState';
import styles from './PinMarker.module.css';

const getCategoryIconUrl = (digitalMap: DigitalMap | null, category: string): string => {
  if (!category || !digitalMap) {
    return '';
  }

  const categorySettings = digitalMap.pin_categories?.find((c) => c.label === category);

  if (!categorySettings) {
    return '';
  }

  return categorySettings.icon_url;
};

interface Props {
  pin: DigitalMapPin;
  isActive: boolean;
}

export const PinDisplay: React.FC<Props> = ({ pin, isActive }) => {
  const { t } = useTranslation();
  const { activePage, selectedItineraryDate } = useMapDisplayContext();
  const { selectedItinerary } = useItineraries();
  const digitalMap = useSelector((state: ReduxState) => state.universal.digitalMap.map);
  const { filters } = useUrlQueryDisplayState();

  const isItineraryActive =
    activePage.startsWith('ITINERARIES') &&
    selectedItinerary?.date_itineraries
      .find((date) => date.date === selectedItineraryDate)
      ?.items.find((item) => item.pin_key === pin.key);

  const iconUrl = pin.icon_url || getCategoryIconUrl(digitalMap, pin.category);

  if (pin.show_wait_time && filters.categories?.find((category) => category === 'WAIT_TIME')) {
    const waitTime = pin.wait_time || 0;

    return (
      <div className={styles['map__pin']}>
        <div
          className={clsx(
            styles['map__pin__circle'],
            styles['large'],
            styles['wait__time'],
            isActive && styles['active'],
            isItineraryActive && styles['active-itinerary']
          )}
        >
          {pin.is_closed ? (
            <div className={styles['closed']}>
              {pin.closed_text ||
                t('Closed', {
                  context: 'waitTime',
                })}
            </div>
          ) : (
            <>
              <div className={styles['min']}>{waitTime}</div>
              <div className={styles['label']}>{t('Min Wait')}</div>
            </>
          )}
        </div>
        <div
          className={clsx(
            styles['map__pin__arrow'],
            styles['large'],
            isActive && styles['active'],
            isItineraryActive && styles['active-itinerary']
          )}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="17"
            viewBox="0 0 22 17"
            fill="none"
          >
            <path d="M4.38862 2.49414L9.78302 11.8998C10.1511 12.5415 11.0643 12.5753 11.4788 11.9626L17.8839 2.49414" />
          </svg>
        </div>
      </div>
    );
  }

  if (pin.use_thumbnail_image_pin_marker) {
    let thumbnailUrl = pin.thumbnail_url;
    if (pin.media_items?.length) {
      const thumbnailImgUrl = pin.media_items?.find((media) => media.type === 'IMAGE')?.url;
      if (thumbnailImgUrl) {
        thumbnailUrl = thumbnailImgUrl;
      }
    }

    return (
      <div className={styles['map__pin']}>
        <Image
          priority
          alt="pin"
          className={clsx(
            styles['map__pin__circle'],
            styles['large'],
            isActive && styles['active'],
            isItineraryActive && styles['active-itinerary']
          )}
          src={thumbnailUrl}
          width={94}
          height={94}
        />
        <div
          className={clsx(
            styles['map__pin__arrow'],
            styles['large'],
            isActive && styles['active'],
            isItineraryActive && styles['active-itinerary']
          )}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="17"
            viewBox="0 0 22 17"
            fill="none"
          >
            <path d="M4.38862 2.49414L9.78302 11.8998C10.1511 12.5415 11.0643 12.5753 11.4788 11.9626L17.8839 2.49414" />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['map__pin']}>
      <Image
        priority
        src={iconUrl}
        alt="pin"
        height={45}
        width={45}
        className={clsx(
          styles['map__pin__circle'],
          styles['small'],
          isActive && styles['active'],
          isItineraryActive && styles['active-itinerary']
        )}
        style={{
          padding: pin.icon_image_fit === 'COVER' ? '0' : '8px',
        }}
      />
      <div
        className={clsx(
          styles['map__pin__arrow'],
          styles['small'],
          isActive && styles['active'],
          isItineraryActive && styles['active-itinerary']
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="22"
          height="17"
          viewBox="0 0 22 17"
          fill="none"
        >
          <path d="M4.38862 2.49414L9.78302 11.8998C10.1511 12.5415 11.0643 12.5753 11.4788 11.9626L17.8839 2.49414" />
        </svg>
      </div>
    </div>
  );
};
